import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Chip
} from '@mui/material'
import StorefrontIcon from '@mui/icons-material/Storefront'
import CloseIcon from '@mui/icons-material/Close'
import { stepperStyles } from '../styles'
import { useApi } from '../../../../hooks/useApi'
import { useWarehouses } from '../../../../hooks/useWarehouses'
import { WorkOrder } from '../../../../interfaces'
import { Warehouse } from '../../../../interfaces/Warehouse/Warehouse'
import { Status } from '../../../../hooks/useFetchState'
import { useUser } from '../../../../hooks/useUser'
import { StepProps } from '../../../../components/_WMS/Stepper'
import { eInventoryCheckSteps, SharedStateType } from '../../types'
import { eInventoryCheckState } from '../../../../enums/eInventoryCheck'
import { ApiResponse } from '../../../../typings/ApiResponse'
import { InventoryCheckCard, InventoryCheckCardItem, StepLoader } from '../../components'
import {
  getAllCyclicals,
  getInventoryCheckUserDetail,
  assignUser,
  getAvailablePositions,
  getTakenPositions
} from '../../api'

interface CyclicalItem extends InventoryCheckCardItem {
  priority?: number
}

type SortMode = 'priority' | 'warehouse'

const ACTIVE_STATES = new Set([
  eInventoryCheckState['Creada'],
  eInventoryCheckState['En Proceso']
])

export const AllCyclicalsScreen: React.FC<StepProps<SharedStateType>> = ({
  setSharedState,
  goToStep
}) => {
  // API para todos los cíclicos (modo prioridad)
  const { state: allCyclicalsState, callApi: callAllCyclicalsApi } =
    useApi<ApiResponse<CyclicalItem>>()
  // API para cíclicos filtrados por bodega
  const { state: warehouseCyclicalsState, callApi: callWarehouseCyclicalsApi } =
    useApi<ApiResponse<CyclicalItem>>()
  const { state: workOrderState, callApi: callWorkOrderApi } =
    useApi<WorkOrder>()
  const { state: changeUserState, callApi: callAssignUserApi } =
    useApi<WorkOrder>()
  const { callApi: callAvailableApi } = useApi<ApiResponse<any>>()
  const { callApi: callTakenApi } = useApi<ApiResponse<any>>()

  const { warehousesState, getWarehouses } = useWarehouses()
  const user = useUser()

  const [sortMode, setSortMode] = useState<SortMode>('warehouse')
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)

  // Carga inicial: todos los cíclicos para el modo prioridad
  useEffect(() => {
    callAllCyclicalsApi(getAllCyclicals()).catch(() => undefined)
  }, [])

  // Carga de bodegas la primera vez que se necesitan
  useEffect(() => {
    if (sortMode === 'warehouse' && !warehousesState.warehouses?.length) {
      getWarehouses()
    }
  }, [sortMode])

  useEffect(() => {
    setSharedState((prev) => ({ ...prev, stepTitle: 'Buscar conteo' }))
    return () => {
      setSharedState((prev) => ({ ...prev, stepTitle: undefined }))
    }
  }, [])


  const handleSetSortMode = (mode: SortMode) => {
    setSortMode(mode)
    if (mode === 'priority') setSelectedWarehouse(null)
  }

  const handleSelectWarehouse = (warehouse: Warehouse) => {
    setSelectedWarehouse(warehouse)
    callWarehouseCyclicalsApi(getAllCyclicals(warehouse.id)).catch(() => undefined)
  }

  const handleClearWarehouse = () => {
    setSelectedWarehouse(null)
  }

  const toggleExpanded = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  const handleSelectCyclical = async (inventoryCheckId: number) => {
    const userId = user?.userDataState?.userData?.userId || 0
    try {
      // 1. Asignar usuario primero (requerido por el backend antes de consultar posiciones)
      await callAssignUserApi(assignUser(inventoryCheckId, userId))

      // 2. Verificar si el usuario tiene posiciones tomadas o disponibles
      const taken = await callTakenApi(getTakenPositions(inventoryCheckId, userId, 1))
      const hasTaken = (taken?.items?.length ?? 0) > 0

      if (!hasTaken) {
        const available = await callAvailableApi(
          getAvailablePositions(inventoryCheckId, userId, 1)
        )
        const hasAvailable = (available?.items?.length ?? 0) > 0

        if (!hasAvailable) {
          setSharedState((prev) => ({
            ...prev,
            workOrderId: undefined,
            error:
              'No tienes posiciones disponibles en este conteo. Todas las ubicaciones están asignadas a otros usuarios o ya fueron auditadas.'
          }))
          goToStep(eInventoryCheckSteps.Error)
          return
        }
      }

      // 3. Cargar detalle y navegar
      const response: WorkOrder = await callWorkOrderApi(
        getInventoryCheckUserDetail(inventoryCheckId, userId)
      )
      if (!response) throw new Error('WorkOrder no existe')
      setSharedState((prev) => ({ ...prev, workOrderId: inventoryCheckId }))
      goToStep(eInventoryCheckSteps.ShowInventoryCheckPositions)
    } catch (ex) {
      const errorMessage = ex instanceof Error ? ex.message : String(ex)
      setSharedState((prev) => ({
        ...prev,
        workOrderId: undefined,
        error: errorMessage
      }))
      goToStep(eInventoryCheckSteps.Error)
    }
  }

  const isLoadingWorkOrder =
    workOrderState.status === Status.Loading ||
    changeUserState.status === Status.Loading

  // Elige el estado API correcto según el modo
  const activeCyclicalsState =
    sortMode === 'warehouse' && selectedWarehouse
      ? warehouseCyclicalsState
      : allCyclicalsState

  const displayedItems = [...(activeCyclicalsState.data?.items ?? [])]
    .filter((item) => ACTIVE_STATES.has(item.stateId))
    .sort((a, b) => {
      if (sortMode === 'warehouse')
        return (a.warehouseName ?? '').localeCompare(b.warehouseName ?? '')
      return (a.priority ?? 999) - (b.priority ?? 999)
    })

  const activeWarehouses = (warehousesState.warehouses ?? []).filter(
    (w) => w.active
  )

  const showWarehousePicker = sortMode === 'warehouse' && !selectedWarehouse
  const showResults =
    sortMode === 'priority' || (sortMode === 'warehouse' && !!selectedWarehouse)

  return (
    <Box sx={stepperStyles.root}>
      {isLoadingWorkOrder && <StepLoader message='Cargando conteo...' />}
      <Box
        sx={stepperStyles.content}
        style={{ justifyContent: 'flex-start', paddingTop: 8, overflowY: 'auto' }}
      >
        <Box style={{ width: '100%', padding: '0 16px' }}>

          {/* Filtros */}
          <Box
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 16,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              marginBottom: 16
            }}
          >
            <Typography
              variant='caption'
              style={{
                color: '#999',
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase',
                display: 'block',
                marginBottom: 8
              }}
            >
              Buscar por
            </Typography>

            <Box style={{ display: 'flex', gap: 8 }}>
              <Button
                variant={sortMode === 'warehouse' ? 'contained' : 'outlined'}
                color='primary'
                size='small'
                onClick={() => handleSetSortMode('warehouse')}
                style={{ borderRadius: 24, textTransform: 'none', flex: 1 }}
              >
                Almacén
              </Button>
              <Button
                variant={sortMode === 'priority' ? 'contained' : 'outlined'}
                color='primary'
                size='small'
                onClick={() => handleSetSortMode('priority')}
                style={{ borderRadius: 24, textTransform: 'none', flex: 1 }}
              >
                Prioridad
              </Button>
            </Box>

            {/* Chip de bodega seleccionada */}
            {selectedWarehouse && (
              <Box style={{ marginTop: 10 }}>
                <Chip
                  icon={<StorefrontIcon style={{ fontSize: 16 }} />}
                  label={selectedWarehouse.warehouseName}
                  color='primary'
                  size='small'
                  onDelete={handleClearWarehouse}
                  deleteIcon={<CloseIcon style={{ fontSize: 14 }} />}
                  style={{ fontWeight: 600 }}
                />
              </Box>
            )}
          </Box>

          {/* ── Picker de bodegas ── */}
          {showWarehousePicker && (
            <>
              <Typography
                variant='caption'
                style={{
                  color: '#999',
                  fontWeight: 700,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  display: 'block',
                  marginBottom: 8
                }}
              >
                Selecciona un almacén
              </Typography>

              {!warehousesState.warehouses ? (
                <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : activeWarehouses.length === 0 ? (
                <Typography
                  variant='body2'
                  style={{ textAlign: 'center', color: '#888', marginTop: 32 }}
                >
                  No hay almacenes disponibles
                </Typography>
              ) : (
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {activeWarehouses.map((warehouse) => (
                    <Button
                      key={warehouse.id}
                      fullWidth
                      variant='outlined'
                      color='primary'
                      onClick={() => handleSelectWarehouse(warehouse)}
                      style={{
                        borderRadius: 12,
                        textTransform: 'none',
                        padding: '14px 16px',
                        justifyContent: 'space-between',
                        background: '#fff'
                      }}
                    >
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <StorefrontIcon style={{ fontSize: 18, opacity: 0.7 }} />
                        <Typography variant='body2' style={{ fontWeight: 700 }}>
                          {warehouse.warehouseName}
                        </Typography>
                      </Box>
                    </Button>
                  ))}
                </Box>
              )}
            </>
          )}

          {/* ── Resultados ── */}
          {showResults && (
            <>
              <Typography
                variant='caption'
                style={{
                  color: '#999',
                  fontWeight: 700,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  display: 'block',
                  marginBottom: 8
                }}
              >
                Resultados
              </Typography>

              {activeCyclicalsState.status === Status.Loading && (
                <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
                  <CircularProgress size={28} />
                </Box>
              )}

              {activeCyclicalsState.status === Status.Success &&
                displayedItems.length === 0 && (
                  <Typography
                    variant='body2'
                    style={{ textAlign: 'center', color: '#888', marginTop: 32 }}
                  >
                    No hay cíclicos disponibles
                    {selectedWarehouse ? ` en ${selectedWarehouse.warehouseName}` : ''}
                  </Typography>
                )}

              {activeCyclicalsState.status === Status.Success &&
                displayedItems.map((item, index) => (
                  <InventoryCheckCard
                    key={item.inventoryCheckId}
                    item={item}
                    index={index}
                    isExpanded={expandedId === item.inventoryCheckId}
                    onToggle={() => toggleExpanded(item.inventoryCheckId)}
                    onSelect={() => handleSelectCyclical(item.inventoryCheckId)}
                    disabled={isLoadingWorkOrder}
                  />
                ))}
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}
